import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePrivy } from '@privy-io/react-auth';
import styled from 'styled-components';
import ConsoleInterface from '../components/ConsoleInterface';
import { useAuth } from '../contexts/AuthContext';
import DashboardLayout from '../components/DashboardLayout';

const HomeContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: ${props => props.theme.colors.background};
  overflow: hidden;
  position: relative;
  flex: 1;
`;

const Header = styled.header`
  width: 100%;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  z-index: 1;
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const Title = styled.h1`
  font-size: 2.5rem;
  font-weight: bold;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1rem;
  text-shadow: 0 0 10px rgba(0, 192, 239, 0.5);
  
  @media (max-width: 768px) {
    font-size: 1.8rem;
    margin-bottom: 0.5rem;
    text-align: center;
  }
`;

const Subtitle = styled.p`
  font-size: 1.2rem;
  color: ${props => props.theme.colors.text};
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    font-size: 1rem;
    margin-bottom: 1rem;
    text-align: center;
  }
`;

const AuthButton = styled.button`
  background-color: ${props => props.theme.colors.primary};
  color: ${props => props.theme.colors.background};
  border: none;
  padding: 0.8rem 2rem;
  font-size: 1.2rem;
  border-radius: 4px;
  cursor: pointer;
  font-family: ${props => props.theme.fonts.main};
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 10;
  box-shadow: 0 0 20px rgba(0, 192, 239, 0.7);
  transition: all 0.3s ease;

  &:hover {
    background-color: ${props => props.theme.colors.accent};
    box-shadow: 0 0 30px rgba(0, 192, 239, 0.9);
  }
  
  @media (max-width: 768px) {
    padding: 0.7rem 1.5rem;
    font-size: 1rem;
    width: 80%;
    max-width: 300px;
    text-align: center;
  }
`;

const AuthOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.7);
  z-index: 5;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const RecoverButton = styled.button`
  background: none;
  border: 1px solid ${props => props.theme.colors.primary};
  color: ${props => props.theme.colors.primary};
  padding: 0.5rem 1rem;
  margin-top: 1rem;
  font-size: 0.9rem;
  border-radius: 4px;
  cursor: pointer;
  font-family: ${props => props.theme.fonts.main};
  transition: all 0.3s ease;

  &:hover {
    background-color: rgba(0, 192, 239, 0.1);
  }
  
  @media (max-width: 768px) {
    padding: 0.5rem 0.8rem;
    font-size: 0.8rem;
  }
`;

const Footer = styled.footer`
  width: 100%;
  padding: 1rem;
  display: flex;
  justify-content: space-between;
  font-size: 0.8rem;
  color: ${props => props.theme.colors.text};
  opacity: 0.7;
  z-index: 2;
  margin-top: auto;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    padding: 0.5rem;
    gap: 0.3rem;
  }
`;

const FooterLink = styled.a`
  color: ${props => props.theme.colors.primary};
  text-decoration: none;
  margin: 0 0.5rem;

  &:hover {
    text-decoration: underline;
  }
  
  @media (max-width: 768px) {
    margin: 0 0.3rem;
    font-size: 0.7rem;
  }
`;

const ErrorMessage = styled.div`
  color: ${props => props.theme.colors.error};
  margin-top: 1rem;
  padding: 0.5rem;
  background-color: rgba(255, 85, 85, 0.1);
  border-radius: 4px;
  text-align: center;
  
  @media (max-width: 768px) {
    width: 80%;
    font-size: 0.8rem;
  }
`;

// Social media icons
const XIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const TelegramIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
    <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.96 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
  </svg>
);

const SocialLink = styled.a`
  color: ${props => props.theme.colors.primary};
  text-decoration: none;
  margin: 0 0.5rem;
  display: flex;
  align-items: center;
  gap: 5px;
  transition: color 0.2s ease;

  &:hover {
    color: ${props => props.theme.colors.accent};
  }
  
  @media (max-width: 768px) {
    margin: 0 0.3rem;
    font-size: 0.7rem;
  }
`;

const SocialLinks = styled.div`
  display: flex;
  align-items: center;
  margin-top: 0.5rem;
  
  @media (max-width: 768px) {
    margin-top: 0.3rem;
  }
`;

// Blockchain explorer icons
const SolscanIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
    <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm-1.243 18.788L6.55 15.54l4.207-2.436 4.208 2.436-4.208 3.248zm.001-5.248L6.55 10.292l4.208-7.292 4.208 7.292-4.208 3.248z" />
  </svg>
);

const DexscreenerIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
    <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm-2 16h4v2h-4v-2zm6-3H8v-2h8v2zm0-3H8V8h8v2z" />
  </svg>
);

// New styled component for the Login Button in the Header
const HeaderLoginButton = styled.button`
  background-color: ${props => props.theme.colors.primary};
  color: ${props => props.theme.colors.background};
  border: none;
  padding: 0.7rem 1.5rem;
  font-size: 1rem;
  border-radius: 4px;
  cursor: pointer;
  font-family: ${props => props.theme.fonts.main};
  margin-top: 1rem; // Add some space above the console or below subtitle
  box-shadow: 0 0 15px rgba(0, 192, 239, 0.6);
  transition: all 0.3s ease;

  &:hover {
    background-color: ${props => props.theme.colors.accent};
    box-shadow: 0 0 25px rgba(0, 192, 239, 0.8);
  }
  
  @media (max-width: 768px) {
    padding: 0.6rem 1.2rem;
    font-size: 0.9rem;
  }
`;

// New InfoBox component
const InfoBox = styled.div`
  background-color: rgba(0, 192, 239, 0.1);
  border: 1px solid ${props => props.theme.colors.primary};
  border-radius: 5px;
  padding: 1rem;
  margin-bottom: 1rem;
  color: ${props => props.theme.colors.text};
  text-align: center;
  box-shadow: 0 0 10px rgba(0, 192, 239, 0.3);
  width: 90%;
  
  @media (max-width: 768px) {
    padding: 0.75rem;
    font-size: 0.9rem;
    width: 100%;
  }
`;

const InfoLink = styled.a`
  color: ${props => props.theme.colors.primary};
  text-decoration: underline;
  font-weight: bold;
  transition: color 0.2s ease;
  
  &:hover {
    color: ${props => props.theme.colors.accent};
  }
`;

const Home: React.FC = () => {
  const { login: privyLogin, authenticated, ready, user: privyUser, getAccessToken } = usePrivy();
  const { login, isAuthenticated, error: authError, clearError } = useAuth();
  const navigate = useNavigate();

  // When Privy authentication completes, update our AuthContext
  useEffect(() => {
    const authenticateWithBackend = async () => {
    if (ready && authenticated && privyUser) {
        try {
          const privyUserId = privyUser.id;
          const token = await getAccessToken();
      
          if (token) {
            await login(token, privyUserId);
            // Navigation to '/console' is handled by the useEffect below based on isAuthenticated
          } else {
            // This error will be caught by the generic catch block
            throw new Error('Failed to get access token from Privy');
          }
        } catch (error) {
          console.error('Failed to authenticate with backend:', error);
          // authError will be set by the login function in AuthContext if backend login fails
          // No need to set localError here
        }
    }
    };
    
    authenticateWithBackend();
  }, [ready, authenticated, privyUser, getAccessToken, login, navigate]); // Added navigate to dependencies as it was used before

  // Navigate to console if already authenticated in our context
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/console');
    }
  }, [isAuthenticated, navigate]);

  // Clear errors when component unmounts
  useEffect(() => {
    return () => {
      clearError();
    };
  }, [clearError]);

  const errorToShow = authError; // Simplified, as localError was not used

  return (
    <DashboardLayout activeMenuItem="console">
      <HomeContainer>
        <Header>
          <Title>Voovo TRADING CONSOLE</Title>
          <Subtitle>AI-Powered Trading Bot Interface</Subtitle>
          {!isAuthenticated && (
            <HeaderLoginButton onClick={privyLogin}>
              Connect Wallet / Login
            </HeaderLoginButton>
          )}
        </Header>

        {/* New InfoBox component */}
        <InfoBox>
          VOOVO Launches Holders Reward System, Investment possibility to earn up to 7% daily reward by investing into our utility: <InfoLink href="https://earn.voovo.app/" target="_blank" rel="noopener noreferrer">https://earn.voovo.app</InfoLink>
        </InfoBox>

        <ConsoleInterface onLogin={privyLogin} />

        {errorToShow && (
          <ErrorMessage>
            {errorToShow}
          </ErrorMessage>
        )}

        <Footer>
          <div>
            <span>Version 1.0.0 Beta</span>
            <SocialLinks>
              <SocialLink href="https://x.com/Voovo_study" target="_blank" rel="noopener noreferrer">
                <XIcon /> @Voovo_study
              </SocialLink>
              <SocialLink href="https://t.me/voovo_study" target="_blank" rel="noopener noreferrer">
                <TelegramIcon /> Telegram
              </SocialLink>
              <SocialLink href="https://solscan.io/token/H6dZStmJBb5c6YAsnV5JxTbgwJiLjT5zKc4GeBwcpump" target="_blank" rel="noopener noreferrer">
                <SolscanIcon /> Solscan
              </SocialLink>
              <SocialLink href="https://dexscreener.com/solana/GjkTe6az5xLXpe4NUAtEc1fLxoLkXDH62nUZBxHZtCoC" target="_blank" rel="noopener noreferrer">
                <DexscreenerIcon /> Chart
              </SocialLink>
            </SocialLinks>
          </div>
          <div>
            <FooterLink href="#">Disclaimer</FooterLink>
            <FooterLink href="#">Terms & Conditions</FooterLink>
          </div>
        </Footer>
      </HomeContainer>
    </DashboardLayout>
  );
};

export default Home; 